import useEmblaCarousel from 'embla-carousel-react'
import type React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { Button } from './Button'
import { DotIndicator } from './DotIndicator'

export interface OnboardingProps {
  className?: string
  /**
   * If provided, the component renders this step (controlled mode).
   * If omitted, the component manages the current step internally (uncontrolled mode).
   */
  step?: 1 | 2
  /** Callback when step changes (via swipe). */
  onStepChange?: (next: 1 | 2) => void
  login: () => void
}

export const Onboarding: React.FC<OnboardingProps> = ({ className, step, onStepChange, login }) => {
  type Step = 1 | 2
  const SLIDES: Step[] = [1, 2]
  const totalSlides = SLIDES.length

  // Uncontrolled fallback state
  const [localStep, setLocalStep] = useState<Step>(1)

  const currentStep = (typeof step === 'number' ? step : localStep) as Step

  const setStepSafe = useCallback(
    (next: number) => {
      const clamped = (next <= 1 ? 1 : 2) as Step
      if (onStepChange) onStepChange(clamped)
      else if (typeof step !== 'number') setLocalStep(clamped)
    },
    [onStepChange, step]
  )

  // Embla carousel for headings
  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: false,
    loop: true,
    align: 'center',
    containScroll: 'trimSnaps',
  })

  // Sync external step -> carousel
  useEffect(() => {
    if (!emblaApi) return
    const target = (currentStep - 1) as number
    if (emblaApi.selectedScrollSnap() !== target) emblaApi.scrollTo(target)
  }, [emblaApi, currentStep])

  const [selectedSnap, setSelectedSnap] = useState(0)
  // Sync carousel -> external step and indicator
  useEffect(() => {
    if (!emblaApi) return
    const onSelect = () => {
      const snap = emblaApi.selectedScrollSnap()
      setSelectedSnap(snap)
      const next = SLIDES[Math.min(snap, SLIDES.length - 1)]
      if (next !== currentStep) setStepSafe(next)
    }
    emblaApi.on('select', onSelect)
    // set initial
    onSelect()
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, currentStep, setStepSafe])

  // Auto-rotate every 4s
  useEffect(() => {
    if (!emblaApi) return
    const id = window.setInterval(() => {
      const next = (emblaApi.selectedScrollSnap() + 1) % totalSlides
      emblaApi.scrollTo(next)
    }, 4000)
    // Pause on user interaction, resume afterwards
    const pause = () => window.clearInterval(id)
    emblaApi.on('pointerDown', pause)
    return () => {
      window.clearInterval(id)
      emblaApi.off('pointerDown', pause)
    }
  }, [emblaApi, totalSlides])

  // Headings and descriptions per step (from Figma)
  // desc is rendered per slide below, synchronized with the carousel

  return (
    <div
      className={`bg-white box-border flex flex-col gap-6 items-center justify-center overflow-clip px-6 py-10 rounded-[40px] ${className ?? ''}`}
    >
      {/* Heading */}
      <div className="flex flex-col gap-3 items-center justify-start w-full select-none">
        <div className="w-full overflow-hidden" ref={emblaRef}>
          <div className="flex w-full">
            {SLIDES.map((s) => {
              const content =
                s === 1
                  ? {
                      h1: 'あなただけの',
                      h2: '安全なウォレット',
                      d1: '秘密鍵は自分だけで管理。',
                      d2: '初めての人でも安心して資産を持てます。',
                    }
                  : {
                      h1: 'シンプルで',
                      h2: '直感的な操作',
                      d1: '残高確認も購入も、すべて数タップで完結。',
                      d2: '難しい知識は必要ありません。',
                    }
              return (
                <div key={s} className="min-w-0 shrink-0 grow-0 basis-full px-2">
                  <div className="text-center text-zinc-900 leading-[1.2] text-[24px] font-bold">
                    <p className="mb-0">{content.h1}</p>
                    <p className="mb-0">{content.h2}</p>
                  </div>
                  <div className="mt-2 w-full text-center text-zinc-500 text-[14px] leading-[20px] tracking-[-0.28px]">
                    <p className="mb-0">{content.d1}</p>
                    <p className="mb-0">{content.d2}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Dot indicator */}
      <div className="w-8">
        <DotIndicator state={selectedSnap + 1} count={totalSlides} />
      </div>

      {/* Buttons */}
      <Button onClick={() => login()} label="ログイン" leftIcon={false} rightIcon={false} />

      {/* Terms text */}
      {/**
      <div className="text-center text-[#606060]">
        <p className="leading-[1.4] text-[11px] m-0">
          <span className="font-semibold text-black">利用規約</span>
          <span> と </span>
          <span className="font-semibold text-black">プライバシーポリシー </span>
          <span>に同意の上、続行してください。</span>
        </p>
      </div>
      */}
    </div>
  )
}

export default Onboarding
